/**
 * まなび(5教科クイズ)の `content/v1/quiz/` 読み込み(閲覧専用)。
 *
 * `services/content.ts`(タイピング)と同じ流儀:
 *   - 配信JSONを fetch するだけ。`packages/quiz` には依存しない
 *     (管理画面は「壊れたデータを見つける道具」なので、出題用の厳しい検証を持ち込まない)
 *   - パースは寛容。型が違うフィールドは落とすだけにして、1問壊れても一覧は出す
 *
 * **帯(band)はハードコードしない。** カリキュラムパック
 * (`content/v1/learn/{カリキュラムID}/pack.json`。docs/13 §9)が
 * 「どの学年が・どのファイルで配られるか」の宣言なので、そこから検出する。
 * パックの一覧は `manifest.json` の `files[].kind === 'learn'` から拾う。
 * パックがまだ無い配信(古い content/v1)では、従来どおり `kind === 'quiz'` を走査する。
 */

import { ContentError } from './content';

/** 帯ID(`kinder` / `e1` / `e2` / 将来 `e3`〜)。文字列のまま扱う。 */
export type BandId = string;

/** 教科名(表示名がそのまま識別子。`かず` / `さんすう` など)。 */
export type SubjectName = string;

/** ふりがなの区切り1つ(docs/13 §12。漢字のかたまりにだけ `r` が付く)。 */
export interface QuizTextSegment {
  readonly t: string;
  readonly r?: string;
}

export interface QuizPrompt {
  readonly text: string;
  /** 漢字版の問題文(「もじの ひょうじ = 漢字」で出る控え。docs/13)。 */
  readonly textKanji?: string;
  /** ふりがな(かなのみ)。 */
  readonly ruby?: string;
  /** `text` のふりがな区切り(漢字の部分にだけ ruby を掛けるための控え)。 */
  readonly textSegments?: readonly QuizTextSegment[];
  /** `textKanji` のふりがな区切り。 */
  readonly textKanjiSegments?: readonly QuizTextSegment[];
  /** ふりがなを画面に出さない(読み方そのものを問う問題)。 */
  readonly rubyHidden?: boolean;
  readonly emoji?: string;
  /** 画像アセットの相対パス(将来入る想定。無ければ何も出さない)。 */
  readonly image?: string;
  /** 読み上げ音声の相対パス。 */
  readonly audio?: string;
}

export interface QuizChoice {
  readonly text?: string;
  /** 漢字版の選択肢(e3 以降だけ)。 */
  readonly textKanji?: string;
  readonly emoji?: string;
  readonly image?: string;
  readonly audio?: string;
  readonly correct: boolean;
}

export interface QuizQuestion {
  readonly id: string;
  readonly subject: SubjectName;
  readonly band: BandId;
  readonly type: string;
  readonly prompt: QuizPrompt;
  readonly choices: readonly QuizChoice[];
  readonly explanation?: string;
  /** 漢字版の解説。 */
  readonly explanationKanji?: string;
  readonly skills: readonly string[];
  readonly unit: string;
}

export interface QuizUnit {
  readonly id: string;
  readonly band: BandId;
  readonly subject: SubjectName;
  readonly title: string;
  readonly questionIds: readonly string[];
}

export interface QuizBank {
  readonly schemaVersion: number;
  readonly band: BandId;
  readonly subjects: readonly SubjectName[];
  readonly units: readonly QuizUnit[];
  readonly questions: readonly QuizQuestion[];
}

/** 帯1つぶん(パス・件数は manifest / パック由来)。 */
export interface BandRef {
  readonly band: BandId;
  /** `content/v1/` からの相対パス(例 `quiz/kinder.json`)。 */
  readonly path: string;
  /** manifest 記載の問題数(実データとの食い違いを見つける手がかり)。 */
  readonly count: number;
  /** パックが宣言する学年名(無ければ規則から作る)。 */
  readonly label?: string;
}

/** manifest から見つけたカリキュラムパック1つ。 */
export interface PackRef {
  /** カリキュラムID(例 `jp-mext`)。 */
  readonly curriculum: string;
  /** `content/v1/` からの相対パス(例 `learn/jp-mext/pack.json`)。 */
  readonly path: string;
}

/** パック1つぶんの帯一覧(学年の宣言 + 配信ファイル)。 */
export interface PackContent {
  readonly curriculum: string;
  readonly bands: readonly BandRef[];
}

/** 1帯ぶんの閲覧素材(問題 + 単元 + 逆引き + 不整合)。 */
export interface BandContent {
  readonly band: BandId;
  readonly subjects: readonly SubjectName[];
  readonly units: readonly QuizUnit[];
  readonly questions: readonly QuizQuestion[];
  readonly questionsById: ReadonlyMap<string, QuizQuestion>;
  readonly unitsById: ReadonlyMap<string, QuizUnit>;
  /** 単元の questionIds にあるのに questions に無い問題ID(参照切れ)。 */
  readonly missingQuestionIds: readonly string[];
  /** どの単元の questionIds にも入っていない問題ID(出題されない問題)。 */
  readonly orphanQuestionIds: readonly string[];
}

// ── 帯の表示名・並び順 ────────────────────────────────────
// 一覧は manifest から来るので、ラベルも「規則」から導く(表を持たない)。

const BAND_LABELS: Readonly<Record<string, string>> = {
  kinder: 'ようじ',
};

/** 帯の表示名。`e{N}` は「しょうがく{N}ねん」。未知の帯はIDをそのまま出す。 */
export function bandLabel(band: BandId): string {
  const known = BAND_LABELS[band];
  if (known !== undefined) return known;
  const grade = /^e(\d+)$/.exec(band)?.[1];
  return grade === undefined ? band : `しょうがく${grade}ねん`;
}

/** 並び順の重み。ようじ → 小1 → 小2 … → 未知の帯。 */
function bandOrder(band: BandId): number {
  if (band === 'kinder') return 0;
  const grade = /^e(\d+)$/.exec(band)?.[1];
  return grade === undefined ? 1000 : Number.parseInt(grade, 10);
}

export function sortBands(bands: readonly BandRef[]): readonly BandRef[] {
  return [...bands].sort(
    (a, b) => bandOrder(a.band) - bandOrder(b.band) || a.band.localeCompare(b.band),
  );
}

// ── パース ────────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asStringArray(value: unknown): readonly string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * `manifest.json` からカリキュラムパックを拾う(kind === 'learn' の行だけ)。
 *
 * パックが1つのあいだ画面にセレクタは出さないが、**拾い方は複数前提**にしておく
 * (2つめのパックを配信した日から、コードを触らずに選べるようになる)。
 */
export function parseLearnPacks(raw: unknown): readonly PackRef[] {
  const root = asRecord(raw);
  if (!root) throw new ContentError('manifest: オブジェクトではありません');
  const files = Array.isArray(root.files) ? root.files : [];
  const packs: PackRef[] = [];
  for (const entry of files) {
    const file = asRecord(entry);
    if (!file || file.kind !== 'learn') continue;
    const path = asString(file.path);
    if (path === undefined) continue;
    const curriculum = /^learn\/([^/]+)\/pack\.json$/.exec(path)?.[1];
    if (curriculum === undefined) continue;
    packs.push({ curriculum, path });
  }
  return [...packs].sort((a, b) => a.curriculum.localeCompare(b.curriculum));
}

/**
 * パックの `pack.json` を帯一覧に変換する。
 *
 * パースは寛容(この画面は壊れたデータを**見つける**道具)。
 * 問題数は manifest 由来なので、引数で渡してもらって突き合わせる。
 */
export function parseQuizPack(
  raw: unknown,
  curriculum: string,
  counts: ReadonlyMap<BandId, number> = new Map(),
): PackContent {
  const root = asRecord(raw);
  if (!root) throw new ContentError('カリキュラムパック: オブジェクトではありません');
  /** 学年ID → 表示名(既定言語 → 最初に見つかった言語)。 */
  const labels = new Map<BandId, string>();
  const defaultLocale = asString(root.defaultLocale) ?? 'ja';
  for (const entry of Array.isArray(root.grades) ? root.grades : []) {
    const grade = asRecord(entry);
    const id = grade === null ? undefined : asString(grade.id);
    if (grade === null || id === undefined) continue;
    const label = asRecord(grade.label);
    const text =
      label === null
        ? undefined
        : (asString(label[defaultLocale]) ??
          Object.values(label)
            .map((value) => asString(value))
            .find((value) => value !== undefined));
    if (text !== undefined) labels.set(id, text);
  }

  const bands: BandRef[] = [];
  for (const entry of Array.isArray(root.bands) ? root.bands : []) {
    const item = asRecord(entry);
    const band = item === null ? undefined : asString(item.band);
    if (item === null || band === undefined) continue;
    const label = labels.get(band);
    bands.push({
      band,
      path: asString(item.path) ?? `quiz/${band}.json`,
      count: counts.get(band) ?? 0,
      ...(label === undefined ? {} : { label }),
    });
  }
  return { curriculum, bands: sortBands(bands) };
}

/** `manifest.json` から quiz の帯を拾う(kind === 'quiz' の行だけ)。 */
export function parseQuizManifest(raw: unknown): readonly BandRef[] {
  const root = asRecord(raw);
  if (!root) throw new ContentError('manifest: オブジェクトではありません');
  const files = Array.isArray(root.files) ? root.files : [];
  const refs: BandRef[] = [];
  for (const entry of files) {
    const file = asRecord(entry);
    if (!file || file.kind !== 'quiz') continue;
    const path = asString(file.path);
    if (path === undefined) continue;
    const band = /([^/]+)\.json$/.exec(path)?.[1];
    if (band === undefined) continue;
    refs.push({
      band,
      path,
      count: typeof file.count === 'number' ? file.count : 0,
    });
  }
  return sortBands(refs);
}

/**
 * ふりがなの区切り(docs/13 §12)。
 *
 * 管理画面は**壊れたデータを見つける道具**なので、ここも寛容に読む:
 * 形になっていない要素は落とすだけで、一覧そのものは出す。
 */
function parseSegments(value: unknown): readonly QuizTextSegment[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const segments: QuizTextSegment[] = [];
  for (const entry of value) {
    const raw = asRecord(entry);
    const t = asString(raw?.t);
    if (t === undefined) continue;
    const r = asString(raw?.r);
    segments.push(r === undefined ? { t } : { t, r });
  }
  return segments.length > 0 ? segments : undefined;
}

function parsePrompt(value: unknown): QuizPrompt {
  const raw = asRecord(value) ?? {};
  const textKanji = asString(raw.textKanji);
  const ruby = asString(raw.ruby);
  const textSegments = parseSegments(raw.textSegments);
  const textKanjiSegments = parseSegments(raw.textKanjiSegments);
  const emoji = asString(raw.emoji);
  const image = asString(raw.image);
  const audio = asString(raw.audio);
  return {
    text: asString(raw.text) ?? '',
    ...(textKanji === undefined ? {} : { textKanji }),
    ...(ruby === undefined ? {} : { ruby }),
    ...(textSegments === undefined ? {} : { textSegments }),
    ...(textKanjiSegments === undefined ? {} : { textKanjiSegments }),
    ...(raw.rubyHidden === true ? { rubyHidden: true } : {}),
    ...(emoji === undefined ? {} : { emoji }),
    ...(image === undefined ? {} : { image }),
    ...(audio === undefined ? {} : { audio }),
  };
}

function parseChoices(value: unknown): readonly QuizChoice[] {
  if (!Array.isArray(value)) return [];
  const choices: QuizChoice[] = [];
  for (const entry of value) {
    const raw = asRecord(entry);
    if (!raw) continue;
    const text = asString(raw.text);
    const textKanji = asString(raw.textKanji);
    const emoji = asString(raw.emoji);
    const image = asString(raw.image);
    const audio = asString(raw.audio);
    choices.push({
      ...(text === undefined ? {} : { text }),
      ...(textKanji === undefined ? {} : { textKanji }),
      ...(emoji === undefined ? {} : { emoji }),
      ...(image === undefined ? {} : { image }),
      ...(audio === undefined ? {} : { audio }),
      correct: raw.correct === true,
    });
  }
  return choices;
}

export function parseQuizBank(raw: unknown): QuizBank {
  const root = asRecord(raw);
  if (!root) throw new ContentError('まなびの問題集: オブジェクトではありません');
  const band = asString(root.band);
  if (band === undefined) throw new ContentError('まなびの問題集: band がありません');

  const units: QuizUnit[] = [];
  for (const entry of Array.isArray(root.units) ? root.units : []) {
    const u = asRecord(entry);
    if (!u) continue;
    const id = asString(u.id);
    if (id === undefined) continue;
    units.push({
      id,
      band: asString(u.band) ?? band,
      subject: asString(u.subject) ?? '(教科未設定)',
      title: asString(u.title) ?? id,
      questionIds: asStringArray(u.questionIds) ?? [],
    });
  }

  const questions: QuizQuestion[] = [];
  for (const entry of Array.isArray(root.questions) ? root.questions : []) {
    const q = asRecord(entry);
    if (!q) continue;
    const id = asString(q.id);
    if (id === undefined) continue;
    const explanation = asString(q.explanation);
    const explanationKanji = asString(q.explanationKanji);
    questions.push({
      id,
      subject: asString(q.subject) ?? '(教科未設定)',
      band: asString(q.band) ?? band,
      type: asString(q.type) ?? 'choice',
      prompt: parsePrompt(q.prompt),
      choices: parseChoices(q.choices),
      ...(explanation === undefined ? {} : { explanation }),
      ...(explanationKanji === undefined ? {} : { explanationKanji }),
      skills: asStringArray(q.skills) ?? [],
      unit: asString(q.unit) ?? '',
    });
  }

  // subjects が壊れていても単元の並びから復元する(画面の教科タブを空にしない)。
  const subjects = asStringArray(root.subjects);
  return {
    schemaVersion: typeof root.schemaVersion === 'number' ? root.schemaVersion : 0,
    band,
    subjects: subjects && subjects.length > 0 ? subjects : subjectsOfUnits(units),
    units,
    questions,
  };
}

/** 単元の並び順から教科の並びを作る(重複は最初の1回だけ)。 */
export function subjectsOfUnits(units: readonly QuizUnit[]): readonly SubjectName[] {
  const seen: SubjectName[] = [];
  for (const unit of units) {
    if (!seen.includes(unit.subject)) seen.push(unit.subject);
  }
  return seen;
}

/** 問題集 → 画面用の索引つきデータ。参照切れ・未使用問題もここで洗い出す。 */
export function buildBandContent(bank: QuizBank): BandContent {
  const questionsById = new Map<string, QuizQuestion>();
  for (const question of bank.questions) questionsById.set(question.id, question);

  const unitsById = new Map<string, QuizUnit>();
  const referenced = new Set<string>();
  const missing: string[] = [];
  for (const unit of bank.units) {
    unitsById.set(unit.id, unit);
    for (const id of unit.questionIds) {
      referenced.add(id);
      if (!questionsById.has(id)) missing.push(id);
    }
  }

  const orphans = bank.questions
    .filter((question) => !referenced.has(question.id))
    .map((question) => question.id);

  return {
    band: bank.band,
    subjects: bank.subjects,
    units: bank.units,
    questions: bank.questions,
    questionsById,
    unitsById,
    missingQuestionIds: missing,
    orphanQuestionIds: orphans,
  };
}

// ── 画面から使う小道具 ────────────────────────────────────

/** 教科に属する単元(出現順)。 */
export function unitsOfSubject(content: BandContent, subject: SubjectName): readonly QuizUnit[] {
  return content.units.filter((unit) => unit.subject === subject);
}

/** 問題の検索対象テキスト。 */
function haystack(question: QuizQuestion): string {
  return [
    question.id,
    question.unit,
    question.subject,
    question.prompt.text,
    question.prompt.ruby ?? '',
    question.explanation ?? '',
    ...question.skills,
    ...question.choices.map((choice) => `${choice.text ?? ''} ${choice.emoji ?? ''}`),
  ]
    .join(' ')
    .toLowerCase();
}

export interface QuestionFilter {
  /** 空文字なら教科で絞らない。 */
  readonly subject?: SubjectName;
  /** 空文字なら単元で絞らない。 */
  readonly unit?: string;
  /** 空文字なら検索しない。 */
  readonly query?: string;
}

/**
 * 教科 → 単元 → 検索の順に絞る。
 *
 * 並びは**単元の questionIds 順**(= 子どもに出る順)。単元指定が無いときは
 * JSON の questions 順のまま出す(原本の並びを崩すと差分レビューが辛い)。
 */
export function filterQuestions(
  content: BandContent,
  filter: QuestionFilter,
): readonly QuizQuestion[] {
  const unitId = filter.unit ?? '';
  let list: readonly QuizQuestion[];
  if (unitId !== '') {
    const unit = content.unitsById.get(unitId);
    list = (unit?.questionIds ?? [])
      .map((id) => content.questionsById.get(id))
      .filter((question): question is QuizQuestion => question !== undefined);
  } else {
    const subject = filter.subject ?? '';
    list =
      subject === ''
        ? content.questions
        : content.questions.filter((question) => question.subject === subject);
  }
  const needle = (filter.query ?? '').trim().toLowerCase();
  if (needle === '') return list;
  return list.filter((question) => haystack(question).includes(needle));
}

/** 音声の本数(問題文 + 選択肢)。「読み上げ未生成」を見つけるための指標。 */
export interface AudioCount {
  readonly prompt: number;
  readonly choices: number;
  readonly total: number;
}

export function audioCount(question: QuizQuestion): AudioCount {
  const prompt = question.prompt.audio === undefined ? 0 : 1;
  const choices = question.choices.filter((choice) => choice.audio !== undefined).length;
  return { prompt, choices, total: prompt + choices };
}

/** 正解の選択肢(ちょうど1つのはず)。 */
export function correctChoices(question: QuizQuestion): readonly QuizChoice[] {
  return question.choices.filter((choice) => choice.correct);
}

/** 出題として成立していない問題の理由(空配列なら健全)。 */
export function questionProblems(question: QuizQuestion): readonly string[] {
  const problems: string[] = [];
  if (question.prompt.text === '') problems.push('問題文が空');
  if (question.choices.length < 2) problems.push('選択肢が2つ未満');
  const correct = correctChoices(question).length;
  if (correct !== 1) problems.push(`正解が${String(correct)}個`);
  if (question.unit === '') problems.push('単元未設定');
  return problems;
}

/** 選択肢の表示テキスト(text が無ければ emoji、それも無ければ画像扱い)。 */
export function choiceText(choice: QuizChoice): string {
  return choice.text ?? choice.emoji ?? (choice.image === undefined ? '' : '(画像)');
}

/** skills タグの出現回数(多い順)。 */
export function skillCounts(
  questions: readonly QuizQuestion[],
): readonly (readonly [string, number])[] {
  const counts = new Map<string, number>();
  for (const question of questions) {
    for (const skill of question.skills) counts.set(skill, (counts.get(skill) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

// ── 取得 ──────────────────────────────────────────────────

export type FetchLike = (
  input: string,
) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

export interface QuizLoaderOptions {
  /** 読み込み元(末尾 `/`)。現在の公開版は起動後に決まるので Promise も受け取る。 */
  readonly baseUrl?: string | Promise<string>;
  readonly fetchFn?: FetchLike;
}

export class QuizLoader {
  private readonly baseUrl: Promise<string>;
  private readonly fetchFn: FetchLike;
  private readonly cache = new Map<BandId, Promise<BandContent>>();
  private manifestPromise: Promise<unknown> | null = null;
  private readonly packCache = new Map<string, Promise<PackContent>>();

  constructor(options: QuizLoaderOptions = {}) {
    this.baseUrl = Promise.resolve(options.baseUrl ?? '/content/v1/');
    this.fetchFn = options.fetchFn ?? ((url) => fetch(url));
  }

  private async getJson(relative: string): Promise<unknown> {
    const url = `${await this.baseUrl}${relative}`;
    let response;
    try {
      response = await this.fetchFn(url);
    } catch (cause) {
      throw new ContentError(`${url} の取得に失敗しました: ${String(cause)}`);
    }
    if (!response.ok) {
      throw new ContentError(`${url} の取得に失敗しました (HTTP ${response.status})`);
    }
    return response.json();
  }

  /** manifest(帯の件数とパックの一覧の出どころ)。1回だけ取りに行く。 */
  private manifest(): Promise<unknown> {
    this.manifestPromise ??= this.getJson('manifest.json').catch((error: unknown) => {
      this.manifestPromise = null;
      throw error;
    });
    return this.manifestPromise;
  }

  /** 配信されているカリキュラムパックの一覧(ハードコードしないための入口)。 */
  async listPacks(): Promise<readonly PackRef[]> {
    return parseLearnPacks(await this.manifest());
  }

  /**
   * パック1つぶんの帯一覧。
   *
   * `curriculum` を省略すると最初のパック。パックが1つも無い配信(古い content/v1)
   * では manifest の `kind === 'quiz'` 走査に落ちる。
   */
  async loadPack(curriculum?: string): Promise<PackContent> {
    const manifest = await this.manifest();
    const packs = parseLearnPacks(manifest);
    const ref =
      curriculum === undefined ? packs[0] : packs.find((p) => p.curriculum === curriculum);
    if (!ref) {
      // パック未配信。従来どおり manifest から帯を拾う(管理画面を止めない)。
      return { curriculum: curriculum ?? '', bands: parseQuizManifest(manifest) };
    }
    const cached = this.packCache.get(ref.curriculum);
    if (cached) return cached;
    const counts = new Map(parseQuizManifest(manifest).map((band) => [band.band, band.count]));
    const promise = this.getJson(ref.path)
      .then((raw) => parseQuizPack(raw, ref.curriculum, counts))
      .catch((error: unknown) => {
        this.packCache.delete(ref.curriculum);
        throw error;
      });
    this.packCache.set(ref.curriculum, promise);
    return promise;
  }

  /** 帯の一覧(パック由来)。 */
  async listBands(curriculum?: string): Promise<readonly BandRef[]> {
    return (await this.loadPack(curriculum)).bands;
  }

  /** 1帯ぶんを読む(パスは manifest 由来。未知の帯は `quiz/{band}.json` を試す)。 */
  load(band: BandId, path?: string): Promise<BandContent> {
    const cached = this.cache.get(band);
    if (cached) return cached;
    const promise = (async (): Promise<BandContent> =>
      buildBandContent(parseQuizBank(await this.getJson(path ?? `quiz/${band}.json`))))().catch(
      (error: unknown) => {
        this.cache.delete(band);
        throw error;
      },
    );
    this.cache.set(band, promise);
    return promise;
  }
}
